/**
 * Reusable swatch-based colour picker. Replaces the native
 * `<input type="color">` for the toolbar's text / fill / line colour
 * controls so the user can pick recurring colours in a single click
 * instead of the OS colour wheel.
 *
 * Layout (top → bottom):
 *  - Theme colours: ArchiMate-layer pastels (5 cols × 2 shades)
 *  - Standard colours: 10 vivid + 4 grayscale
 *  - Recent colours: up to 8, persisted to localStorage
 *  - "Meer kleuren…": opens the native picker for a one-off custom value
 *
 * Trigger is rendered by the parent (so the look matches each toolbar
 * slot — A-with-underline for text, square for fill, line stroke for
 * line colour). Parent passes a `renderTrigger` render-prop that gets
 * the click handler and current colour.
 */
import { type CSSProperties, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

/**
 * `window.EyeDropper` — Chromium-based browsers + recent Safari ship a
 * native screen-pixel sampler. Not in lib.dom yet, so we declare a
 * narrow type here and feature-detect at call-time.
 */
interface EyeDropperApi {
  open(options?: { signal?: AbortSignal }): Promise<{ sRGBHex: string }>;
}
interface EyeDropperConstructor {
  new (): EyeDropperApi;
}
declare global {
  interface Window {
    EyeDropper?: EyeDropperConstructor;
  }
}

const RECENTS_KEY = "ae.recentColours";
const MAX_RECENTS = 8;

const THEME_LIGHT = ["#FFF6B0", "#C7DFFC", "#C5E8C5", "#DCC8E5", "#FCDBB7", "#FCEBC7", "#E2E8F0"];
const THEME_DARK = ["#E5C500", "#5B8DEF", "#4FAA5E", "#9F70B5", "#E69A4D", "#D9B66B", "#94A3B8"];

const STANDARD_ROW: string[] = [
  "#FFFFFF",
  "#F1F5F9",
  "#94A3B8",
  "#475569",
  "#000000",
  "#EF4444",
  "#F97316",
  "#FACC15",
  "#22C55E",
  "#06B6D4",
  "#3B82F6",
  "#8B5CF6",
  "#EC4899",
  "#A855F7",
];

function loadRecents(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(RECENTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
  } catch {
    return [];
  }
}

function saveRecents(values: string[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify(values.slice(0, MAX_RECENTS)));
  } catch {
    // Storage may be unavailable; silently ignore.
  }
}

export interface SwatchColorPickerProps {
  /** Currently chosen colour. `undefined` means "no override / default". */
  value: string | undefined;
  /** Called with the new colour, or `undefined` when the user picks "Default". */
  onChange: (color: string | undefined) => void;
  /** Tooltip text on the trigger button. */
  title: string;
  /** Whether the trigger should be disabled (no selection available). */
  disabled?: boolean;
  /**
   * Render-prop for the trigger button. Receives the current colour and
   * an `onClick` handler — the parent decides what the swatch looks like
   * (A underline for text, square for fill, stroke for line, etc.).
   */
  renderTrigger: (args: {
    color: string | undefined;
    onClick: () => void;
    open: boolean;
  }) => React.ReactNode;
  /**
   * Optional: when set, "Default" button at the top clears the colour by
   * calling onChange(undefined). Useful for fontColor / fillColor where
   * `undefined` means "follow the layer palette".
   */
  allowClear?: boolean;
  /**
   * Override the label on the clear-to-default button. Defaults to the
   * i18n key `ribbon.swatch.defaultLabel`. For label-background pickers
   * `"Transparant"` is more accurate than "Default" because the cleared
   * state shows no fill (text-only with halo) rather than a theme colour.
   */
  clearLabel?: string | undefined;
  /**
   * Wanneer `true`, voegt een aparte "Transparant"-knop toe ÓNDER de
   * default/clear-knop. Zinvol bij vulkleur (fillColor) waar de
   * gebruiker wil kunnen kiezen tussen layer-default, expliciet
   * transparant (om descendants of achterliggende objecten te laten
   * doorkomen), of een specifieke kleur — én bij lijnkleur
   * (strokeColor) op drawing-rechthoeken zodat een randloze
   * rechthoek (= vrij tekstblok) gemaakt kan worden.
   *
   * Klik zet `onChange("transparent")` — wordt opgeslagen in viewState
   * als `nodeStyle.fillColor: "transparent"` (transparante wrapper-bg)
   * of `nodeStyle.strokeColor: "transparent"` (onzichtbare border) en
   * door ConceptNode dienovereenkomstig gerenderd.
   */
  allowTransparent?: boolean;
}

export function SwatchColorPicker({
  value,
  onChange,
  title,
  disabled,
  renderTrigger,
  allowClear,
  clearLabel,
  allowTransparent,
}: SwatchColorPickerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [recents, setRecents] = useState<string[]>(() => loadRecents());
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const nativeInputRef = useRef<HTMLInputElement>(null);
  // Popover gets rendered via Portal naar document.body i.p.v. naast de
  // trigger, anders clipt 'm in containers met `overflow: hidden`
  // (zoals het AttributePanel — aside heeft overflow-hidden + de body
  // overflow-y-auto). Position berekenen we vanuit getBoundingClientRect.
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number } | null>(null);

  const recomputePosition = () => {
    const trig = triggerRef.current;
    if (!trig) return;
    const rect = trig.getBoundingClientRect();
    // Default: drop down below the trigger, left-aligned.
    const popoverWidth = 260;
    const popoverHeightEstimate = 320; // good-enough; popover scrolls if larger
    const margin = 4;
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    let left = rect.left;
    let top = rect.bottom + margin;

    // Flip horizontaal als 'ie rechts buiten beeld zou vallen
    if (left + popoverWidth > viewportW - 8) {
      left = Math.max(8, viewportW - popoverWidth - 8);
    }
    // Flip naar boven als 'ie onder buiten beeld zou vallen
    if (top + popoverHeightEstimate > viewportH - 8) {
      const above = rect.top - margin - popoverHeightEstimate;
      if (above >= 8) top = above;
    }
    setPopoverPos({ top, left });
  };

  // Compute position right when the popover opens, plus on scroll/resize
  // so 't blijft staan onder de trigger.
  useLayoutEffect(() => {
    if (!open) {
      setPopoverPos(null);
      return;
    }
    recomputePosition();
    const onScrollOrResize = () => recomputePosition();
    // Capture-phase scroll-listener vangt scroll-events uit elke
    // overflow-container (AttributePanel, settings-pages, etc.) zodat
    // we de popover ook bijhouden wanneer de gebruiker in een paneel
    // scrollt na 'm te openen.
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open]);

  // Close on outside click / Esc. We register in CAPTURE phase so we
  // fire before the canvas's rubber-band handler can call
  // stopPropagation / stopImmediatePropagation on the mousedown — bubble-
  // phase listeners would otherwise never see clicks on the canvas.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (triggerRef.current?.contains(t)) return;
      if (popoverRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onDown, true);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown, true);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const apply = (color: string | undefined) => {
    onChange(color);
    if (color) {
      const next = [color, ...recents.filter((c) => c.toLowerCase() !== color.toLowerCase())];
      const trimmed = next.slice(0, MAX_RECENTS);
      setRecents(trimmed);
      saveRecents(trimmed);
    }
    setOpen(false);
  };

  return (
    <div ref={triggerRef} className="inline-flex">
      {renderTrigger({
        color: value,
        open,
        onClick: () => {
          if (disabled) return;
          setOpen((v) => !v);
        },
      })}
      {open &&
        popoverPos &&
        createPortal(
          // biome-ignore lint/a11y/noStaticElementInteractions: presentationele overlay-laag; sluiten via Escape + knoppen, klik-buiten is muis-gemak
          <div
            ref={popoverRef}
            // Position: fixed via inline style zodat 'ie buiten elke
            // overflow-clip-container valt (AttributePanel, settings).
            // Hoge z-index om boven modals + toolbars uit te komen.
            style={{ position: "fixed", top: popoverPos.top, left: popoverPos.left }}
            className="ledger z-[1000] w-[260px] rounded-md border border-(--ld-control) bg-(--ld-surface) p-2 text-xs shadow-lg"
            // `data-swatch-popover` zorgt dat click-outside-handlers in
            // parent-panelen (AttributePanel, toolbar) clicks binnen het
            // popover-portal niet als "buiten" interpreteren. Capture-
            // phase-listeners zien deze attribuut via target.closest().
            data-swatch-popover="true"
            // Stop event propagation so canvas-level handlers (rubber-band)
            // don't see clicks inside the popover.
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          >
            <div className="mb-1 px-1 font-medium text-(--ld-muted) text-xs">{title}</div>

            {allowClear && (
              <button
                type="button"
                className="mb-2 flex w-full items-center gap-2 rounded border border-(--ld-border) px-2 py-1 text-left text-(--ld-text) hover:bg-(--ld-raised)"
                onClick={() => apply(undefined)}
              >
                {/*
                Visual indicator for "transparent" clears. When the
                caller sets clearLabel to "Transparant", we mirror the
                checkered swatch from the trigger button so the user
                gets a consistent "no fill" cue. Other clear-labels
                (Default/Standaard) get a regular blank swatch.
              */}
                {clearLabel === "Transparant" || clearLabel === t("ribbon.swatch.transparent") ? (
                  // Match the trigger button's preview-box recipe one-to-one
                  // so both visuals read as "transparent / no fill" without
                  // size or contrast drift between trigger and popover.
                  <span
                    className="flex h-5 w-6 shrink-0 items-center justify-center rounded border border-(--ld-control) font-semibold text-xs text-(--ld-text)"
                    style={{
                      backgroundImage:
                        "linear-gradient(45deg, #cbd5e1 25%, transparent 25%, transparent 75%, #cbd5e1 75%), linear-gradient(45deg, #cbd5e1 25%, transparent 25%, transparent 75%, #cbd5e1 75%)",
                      backgroundSize: "6px 6px",
                      backgroundPosition: "0 0, 3px 3px",
                    }}
                    aria-hidden
                  >
                    L
                  </span>
                ) : (
                  <span
                    className="block h-5 w-6 shrink-0 rounded border border-(--ld-control) bg-(--ld-surface)"
                    aria-hidden
                  />
                )}
                {clearLabel ?? t("ribbon.swatch.defaultLabel")}
              </button>
            )}

            {allowTransparent && (
              <button
                type="button"
                className="mb-2 flex w-full items-center gap-2 rounded border border-(--ld-border) px-2 py-1 text-left text-(--ld-text) hover:bg-(--ld-raised)"
                onClick={() => apply("transparent")}
              >
                <span
                  className="flex h-5 w-6 shrink-0 items-center justify-center rounded border border-(--ld-control)"
                  style={{
                    backgroundImage:
                      "linear-gradient(45deg, #cbd5e1 25%, transparent 25%, transparent 75%, #cbd5e1 75%), linear-gradient(45deg, #cbd5e1 25%, transparent 25%, transparent 75%, #cbd5e1 75%)",
                    backgroundSize: "6px 6px",
                    backgroundPosition: "0 0, 3px 3px",
                  }}
                  aria-hidden
                />
                {t("ribbon.swatch.transparent")}
              </button>
            )}

            <Row swatches={THEME_LIGHT} current={value} onPick={apply} />
            <Row swatches={THEME_DARK} current={value} onPick={apply} />

            <div className="mt-2 mb-1 px-1 text-(--ld-muted) text-xs">
              {t("ribbon.swatch.standardColours")}
            </div>
            <Row swatches={STANDARD_ROW} current={value} onPick={apply} />

            {recents.length > 0 && (
              <>
                <div className="mt-2 mb-1 px-1 text-(--ld-muted) text-xs">
                  {t("ribbon.swatch.recent")}
                </div>
                <Row swatches={recents} current={value} onPick={apply} />
              </>
            )}

            <div className="mt-2 border-(--ld-border) border-t pt-2">
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded px-2 py-1 text-(--ld-text) hover:bg-(--ld-raised)"
                onClick={() => nativeInputRef.current?.click()}
              >
                <span aria-hidden>🎨</span>
                {t("ribbon.swatch.moreColours")}
              </button>
              <input
                ref={nativeInputRef}
                type="color"
                className="sr-only"
                value={value ?? "#000000"}
                onChange={(e) => apply(e.target.value)}
              />
              {/*
              Eyedropper — let the user click anywhere on screen to
              sample a colour. Browser-native API; we hide the button
              when unsupported (Firefox at the time of writing).
              Closes the popover during sampling so it doesn't
              obscure the picked area.
            */}
              {typeof window !== "undefined" && typeof window.EyeDropper === "function" && (
                <button
                  type="button"
                  className="mt-1 flex w-full items-center gap-2 rounded px-2 py-1 text-(--ld-text) hover:bg-(--ld-raised)"
                  title={t("ribbon.swatch.eyedropperTitle")}
                  onClick={async () => {
                    const Ctor = window.EyeDropper;
                    if (!Ctor) return;
                    setOpen(false); // close popover so picker can see canvas
                    try {
                      const dropper = new Ctor();
                      const result = await dropper.open();
                      if (result?.sRGBHex) apply(result.sRGBHex);
                    } catch {
                      // User pressed Esc / cancelled — silently ignore.
                    }
                  }}
                >
                  <EyeDropperIcon />
                  {t("ribbon.swatch.eyedropper")}
                </button>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

/** Small pipette icon — used on the eyedropper button. */
function EyeDropperIcon() {
  return (
    <svg
      aria-hidden
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m2 22 1-1h3l9-9" />
      <path d="M3 21v-3l9-9" />
      <path d="m15 6 3.4-3.4a2.121 2.121 0 1 1 3 3L18 9l.4.4a2.121 2.121 0 1 1-3 3l-3.8-3.8a2.121 2.121 0 1 1 3-3l.4.4Z" />
    </svg>
  );
}

function Row({
  swatches,
  current,
  onPick,
}: {
  swatches: string[];
  current: string | undefined;
  onPick: (c: string) => void;
}) {
  return (
    <div className="grid grid-cols-7 gap-1">
      {swatches.map((c) => {
        const isCurrent = current && c.toLowerCase() === current.toLowerCase();
        const style: CSSProperties = { backgroundColor: c };
        return (
          <button
            key={c}
            type="button"
            title={c.toUpperCase()}
            className={`h-6 w-6 rounded border ${isCurrent ? "border-(--ld-accent) ring-2 ring-(--ld-accent)/40" : "border-(--ld-control) hover:border-(--ld-muted)"}`}
            style={style}
            onClick={() => onPick(c)}
          />
        );
      })}
    </div>
  );
}
